import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, Trash2, Search, Calendar, Edit, Mail, Archive, ArchiveRestore, Settings, TrendingUp, ArrowUpDown, LogOut, FileDown, MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import { bookingAPI, settingsAPI } from '../api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Helper to parse date strings, converting from UTC to Australian time if needed
const parseDateString = (dateStr) => {
  if (!dateStr) return new Date();
  
  // If it's a simple YYYY-MM-DD format (new bookings), parse as local date
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // For ISO strings with time (old bookings), convert from UTC to Australian time
  if (dateStr.includes('T')) {
    // Parse as UTC and add 11 hours for Australian Eastern Time
    const utcDate = new Date(dateStr);
    // Add 11 hours to convert UTC to AEDT
    utcDate.setHours(utcDate.getHours() + 11);
    return utcDate;
  }
  
  return new Date(dateStr);
};

const AdminPage = ({ onLogout }) => {
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [archivedBookings, setArchivedBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState({ marrickvillePrice: 30, turrellaPrice: 20 });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  
  const [sortBy, setSortBy] = useState('bookingDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadBookings();
    loadStats();
    loadPricing();
    loadArchivedBookings();
  }, [sortBy, sortOrder]);

  const loadBookings = async () => {
    try {
      const data = await bookingAPI.getAllBookings({ sort_by: sortBy, order: sortOrder });
      setBookings(data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadArchivedBookings = async () => {
    try {
      const data = await bookingAPI.getArchivedBookings();
      setArchivedBookings(data);
    } catch (error) {
      console.error('Error loading archived bookings:', error);
    }
  };

  const loadStats = async () => {
    try {
      const data = await bookingAPI.getBookingStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPricing = async () => {
    try {
      const data = await settingsAPI.getPricing();
      setPricing(data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      await bookingAPI.deleteBooking(id);
      loadBookings();
      loadArchivedBookings();
      loadStats();
      toast({
        title: "Booking Deleted",
        description: "The booking has been removed successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete booking.",
        variant: "destructive"
      });
    }
  };

  const handleArchive = async (id) => {
    try {
      await bookingAPI.archiveBooking(id);
      loadBookings();
      loadArchivedBookings();
      loadStats();
      toast({
        title: "Booking Archived",
        description: "The booking has been archived successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to archive booking.",
        variant: "destructive"
      });
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await bookingAPI.unarchiveBooking(id);
      loadBookings();
      loadArchivedBookings();
      loadStats();
      toast({
        title: "Booking Restored",
        description: "The booking has been restored successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to restore booking.",
        variant: "destructive"
      });
    }
  };

  const handleResendEmail = async (id) => {
    try {
      await bookingAPI.resendConfirmation(id);
      toast({
        title: "Email Sent",
        description: "Confirmation email has been resent to the customer.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const blob = await bookingAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking_confirmation_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "PDF Downloaded",
        description: "Booking confirmation PDF has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download PDF.",
        variant: "destructive"
      });
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setIsDetailOpen(true);
  };

  const handleEditBooking = (booking) => {
    setEditingBooking({...booking});
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await bookingAPI.updateBooking(editingBooking.id, editingBooking);
      loadBookings();
      loadStats();
      setIsEditOpen(false);
      toast({
        title: "Booking Updated",
        description: "The booking has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update booking.",
        variant: "destructive"
      });
    }
  };

  const handleUpdatePricing = async () => {
    try {
      await settingsAPI.updatePricing(pricing);
      setIsPricingOpen(false);
      toast({
        title: "Pricing Updated",
        description: "Parking rates have been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update pricing.",
        variant: "destructive"
      });
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch = 
      booking.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'cancelled':
        return 'bg-red-100 text-red-800 hover:bg-red-100';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const BookingsTable = ({ bookingsList, isArchived = false }) => (
    <div className="overflow-x-auto -mx-4 md:mx-0">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead 
              className="cursor-pointer hover:bg-slate-100 whitespace-nowrap"
              onClick={() => toggleSort('bookingDate')}
            >
              <div className="flex items-center gap-1">
                <span className="hidden sm:inline">Booking </span>Date
                <ArrowUpDown size={14} />
              </div>
            </TableHead>
            <TableHead>Customer</TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-slate-100 whitespace-nowrap"
              onClick={() => toggleSort('entryDate')}
            >
              <div className="flex items-center gap-1">
                Entry
                <ArrowUpDown size={14} />
              </div>
            </TableHead>
            <TableHead className="whitespace-nowrap">Exit</TableHead>
            <TableHead className="hidden md:table-cell">Location</TableHead>
            <TableHead className="text-center hidden md:table-cell">Cars</TableHead>
            <TableHead className="text-right hidden sm:table-cell">Total</TableHead>
            <TableHead className="text-center hidden md:table-cell">Status</TableHead>
            <TableHead className="text-center w-12">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookingsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                No bookings found
              </TableCell>
            </TableRow>
          ) : (
            bookingsList.map((booking) => (
              <TableRow key={booking.id} className="hover:bg-slate-50">
                <TableCell className="font-medium whitespace-nowrap text-sm">
                  {format(parseDateString(booking.bookingDate), 'dd MMM')}
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{booking.fullName}</div>
                    <div className="text-xs text-gray-500 hidden sm:block">{booking.email}</div>
                    <div className="text-xs text-gray-500 sm:hidden">{booking.location} - ${booking.total.toFixed(0)}</div>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">
                  {format(parseDateString(booking.entryDate), 'dd MMM')}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">
                  {format(parseDateString(booking.exitDate), 'dd MMM')}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <span className="font-medium text-teal-700">{booking.location}</span>
                </TableCell>
                <TableCell className="text-center hidden md:table-cell">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {booking.numberOfCars}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-bold text-teal-600 whitespace-nowrap hidden sm:table-cell">
                  ${booking.total.toFixed(2)}
                </TableCell>
                <TableCell className="text-center hidden md:table-cell">
                  <Badge className={getStatusColor(booking.status)}>
                    {booking.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {/* Desktop: Show all buttons in a row */}
                  <div className="hidden md:flex justify-center gap-1 flex-nowrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(booking)}
                      className="bg-teal-50 text-teal-600 border-teal-300 hover:bg-teal-100 hover:text-teal-700 hover:border-teal-500"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPDF(booking.id)}
                      className="bg-cyan-50 text-cyan-600 border-cyan-300 hover:bg-cyan-100 hover:text-cyan-700 hover:border-cyan-500"
                      title="Download PDF"
                    >
                      <FileDown size={16} />
                    </Button>
                    {!isArchived && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditBooking(booking)}
                          className="bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100 hover:text-blue-700 hover:border-blue-500"
                          title="Edit Booking"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendEmail(booking.id)}
                          className="bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100 hover:text-purple-700 hover:border-purple-500"
                          title="Resend Email"
                        >
                          <Mail size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArchive(booking.id)}
                          className="bg-orange-50 text-orange-600 border-orange-300 hover:bg-orange-100 hover:text-orange-700 hover:border-orange-500"
                          title="Archive Booking"
                        >
                          <Archive size={16} />
                        </Button>
                      </>
                    )}
                    {isArchived && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnarchive(booking.id)}
                        className="bg-green-50 text-green-600 border-green-300 hover:bg-green-100 hover:text-green-700 hover:border-green-500"
                        title="Restore Booking"
                      >
                        <ArchiveRestore size={16} />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(booking.id)}
                      className="bg-red-50 text-red-600 border-red-300 hover:bg-red-100 hover:text-red-700 hover:border-red-500"
                      title="Delete Booking"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  {/* Mobile: Dropdown menu */}
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => handleViewDetails(booking)}>
                          <Eye size={16} className="mr-2 text-teal-600" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadPDF(booking.id)}>
                          <FileDown size={16} className="mr-2 text-cyan-600" />
                          Download PDF
                        </DropdownMenuItem>
                        {!isArchived && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditBooking(booking)}>
                              <Edit size={16} className="mr-2 text-blue-600" />
                              Edit Booking
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResendEmail(booking.id)}>
                              <Mail size={16} className="mr-2 text-purple-600" />
                              Resend Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleArchive(booking.id)}>
                              <Archive size={16} className="mr-2 text-orange-600" />
                              Archive
                            </DropdownMenuItem>
                          </>
                        )}
                        {isArchived && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleUnarchive(booking.id)}>
                              <ArchiveRestore size={16} className="mr-2 text-green-600" />
                              Restore
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(booking.id)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="border-none shadow-xl mb-6">
          <CardHeader className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <CardTitle className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <Calendar size={32} />
                Admin Dashboard - Parking Bookings
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={() => setIsPricingOpen(true)}
                  className="bg-white text-teal-700 hover:bg-teal-50"
                >
                  <Settings className="mr-2" size={18} />
                  Update Pricing
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  className="bg-white text-red-600 hover:bg-red-50 border-white"
                >
                  <LogOut className="mr-2" size={18} />
                  Logout
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Stats Cards */}
        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="text-sm text-gray-600 mb-1">Total Bookings</div>
                  <div className="text-3xl font-bold text-teal-600">{stats.totalBookings}</div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="text-sm text-gray-600 mb-1">Confirmed</div>
                  <div className="text-3xl font-bold text-green-600">{stats.confirmed}</div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="text-sm text-gray-600 mb-1">Pending</div>
                  <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
                  <div className="text-3xl font-bold text-teal-600">${stats.totalRevenue.toFixed(2)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Location Revenue Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Marrickville Revenue</div>
                      <div className="text-2xl font-bold text-blue-700">${stats.marrickvilleRevenue.toFixed(2)}</div>
                      <div className="text-xs text-gray-600 mt-1">{stats.marrickvilleCount} active bookings</div>
                      {stats.allTimeMarrickvilleRevenue > stats.marrickvilleRevenue && (
                        <div className="text-xs text-blue-600 mt-1 font-semibold">
                          All-time: ${stats.allTimeMarrickvilleRevenue?.toFixed(2) || '0.00'}
                        </div>
                      )}
                    </div>
                    <TrendingUp size={40} className="text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Turrella Revenue</div>
                      <div className="text-2xl font-bold text-purple-700">${stats.turrellaRevenue.toFixed(2)}</div>
                      <div className="text-xs text-gray-600 mt-1">{stats.turrellaCount} active bookings</div>
                      {stats.allTimeTurrellaRevenue > stats.turrellaRevenue && (
                        <div className="text-xs text-purple-600 mt-1 font-semibold">
                          All-time: ${stats.allTimeTurrellaRevenue?.toFixed(2) || '0.00'}
                        </div>
                      )}
                    </div>
                    <TrendingUp size={40} className="text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Archived Revenue Stats - Only show if there are archived bookings */}
            {stats.archivedCount > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="border-none shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 mb-1">Archived - Marrickville</div>
                    <div className="text-lg font-bold text-gray-600">${stats.archivedMarrickvilleRevenue?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-500">{stats.archivedMarrickvilleCount || 0} bookings</div>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 mb-1">Archived - Turrella</div>
                    <div className="text-lg font-bold text-gray-600">${stats.archivedTurrellaRevenue?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-500">{stats.archivedTurrellaCount || 0} bookings</div>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 mb-1">All-Time Total Revenue</div>
                    <div className="text-lg font-bold text-green-700">${stats.allTimeTotalRevenue?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-500">Active + Archived</div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Filters */}
        <Card className="border-none shadow-lg mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by customer name, email, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table with Tabs */}
        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="active">Active Bookings</TabsTrigger>
                <TabsTrigger value="archived">Archived Bookings</TabsTrigger>
              </TabsList>
              <TabsContent value="active">
                <BookingsTable bookingsList={filteredBookings} />
              </TabsContent>
              <TabsContent value="archived">
                <BookingsTable bookingsList={archivedBookings} isArchived={true} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Booking Details Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-teal-700">
              Booking Details
            </DialogTitle>
            <DialogDescription>
              Complete information for this parking reservation
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Booking ID</p>
                  <p className="font-semibold">{selectedBooking.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Booking Date</p>
                  <p className="font-semibold">
                    {format(parseDateString(selectedBooking.bookingDate), 'PPP')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Customer Name</p>
                  <p className="font-semibold">{selectedBooking.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Email</p>
                  <p className="font-semibold">{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Phone</p>
                  <p className="font-semibold">{selectedBooking.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Location</p>
                  <p className="font-semibold text-teal-700">{selectedBooking.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Entry Date</p>
                  <p className="font-semibold">
                    {format(parseDateString(selectedBooking.entryDate), 'PPP')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Exit Date</p>
                  <p className="font-semibold">
                    {format(parseDateString(selectedBooking.exitDate), 'PPP')}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Car Registrations</p>
                  <p className="font-semibold">{selectedBooking.carRegistrations?.join(', ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Number of Cars</p>
                  <p className="font-semibold">{selectedBooking.numberOfCars}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Transfer Service</p>
                  <p className="font-semibold">
                    {selectedBooking.transferService ? 'Yes ($200)' : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <Badge className={getStatusColor(selectedBooking.status)}>
                    {selectedBooking.status}
                  </Badge>
                </div>
                {selectedBooking.gateCode && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Gate Code</p>
                    <p className="font-semibold text-lg text-teal-600">{selectedBooking.gateCode}</p>
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Parking Subtotal:</span>
                    <span className="font-semibold">${selectedBooking.parkingSubtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">GST (10%):</span>
                    <span className="font-semibold">${selectedBooking.gst?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-2">
                    <span className="text-xl font-semibold text-gray-700">Total Amount</span>
                    <span className="text-3xl font-bold text-teal-600">
                      ${selectedBooking.total?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-teal-700">
              Edit Booking
            </DialogTitle>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={editingBooking.fullName}
                    onChange={(e) => setEditingBooking({...editingBooking, fullName: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingBooking.email}
                    onChange={(e) => setEditingBooking({...editingBooking, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editingBooking.phone}
                    onChange={(e) => setEditingBooking({...editingBooking, phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={editingBooking.status} 
                    onValueChange={(value) => setEditingBooking({...editingBooking, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-teal-600 hover:bg-teal-700">
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pricing Update Dialog */}
      <Dialog open={isPricingOpen} onOpenChange={setIsPricingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-teal-700">
              Update Pricing
            </DialogTitle>
            <DialogDescription>
              Change the daily parking rates and transfer service price
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Marrickville Price (per day)</Label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  type="number"
                  value={pricing.marrickvillePrice}
                  onChange={(e) => setPricing({...pricing, marrickvillePrice: parseInt(e.target.value) || 0})}
                  className="text-lg"
                />
              </div>
            </div>
            <div>
              <Label>Turrella Price (per day)</Label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  type="number"
                  value={pricing.turrellaPrice}
                  onChange={(e) => setPricing({...pricing, turrellaPrice: parseInt(e.target.value) || 0})}
                  className="text-lg"
                />
              </div>
            </div>
            <div className="border-t pt-4">
              <Label>Car Transfer Service Price</Label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  type="number"
                  value={pricing.transferServicePrice || 200}
                  onChange={(e) => setPricing({...pricing, transferServicePrice: parseInt(e.target.value) || 0})}
                  className="text-lg"
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">Price for private car transfer to/from cruise terminals</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPricingOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePricing} className="bg-teal-600 hover:bg-teal-700">
              Update Pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
